/** Client-owned onboarding configuration. This is not an employee or security-role model. */
export interface OnboardingNamedItem {
  id: string;
  name: string;
  active: boolean;
}

export interface OnboardingLocation extends OnboardingNamedItem {
  address: string | null;
}

export interface OnboardingProfileAccess {
  microsoft365Required: boolean;
  microsoft365License: string | null;
  officeAppsRequired: boolean;
  teamsRequired: boolean;
  vpnRequired: boolean;
  phoneRequired: boolean;
  printerAccessRequired: boolean;
  notes: string | null;
}

export interface OnboardingProfileHardware {
  desktopRequired: boolean;
  laptopRequired: boolean;
  dockingStationRequired: boolean;
  monitorCount: number;
  keyboardMouseRequired: boolean;
  headsetRequired: boolean;
  deskPhoneRequired: boolean;
  scannerRequired: boolean;
  printerAccessRequired: boolean;
  notes: string | null;
}

export interface OnboardingProfileSoftware {
  id: string;
  softwareName: string;
  systemDetail?: string | null;
  installRequired: boolean;
  licenseRequired: boolean;
  notes: string | null;
}

export interface OnboardingProfileSharedDrive {
  id: string;
  driveName: string;
  drivePath: string | null;
  /** Legacy fields accepted for compatibility, not used by the configurator/checklist. */
  accessLevel?: string | null;
  notes?: string | null;
}

export interface OnboardingProfileGroup {
  id: string;
  groupName: string;
  systemName: string | null;
  groupType: string | null;
  notes: string | null;
}

export interface OnboardingProfileCustomAccess extends OnboardingNamedItem {
  notes: string | null;
}

export interface OnboardingProfileCustomHardware extends OnboardingNamedItem {
  quantity: number;
  notes: string | null;
}

export interface OnboardingProfile extends OnboardingNamedItem {
  description: string | null;
  access: OnboardingProfileAccess;
  /** Older payloads omit printer defaults. */
  printers?: readonly OnboardingNamedItem[];
  customAccess: readonly OnboardingProfileCustomAccess[];
  software: readonly OnboardingProfileSoftware[];
  sharedDrives: readonly OnboardingProfileSharedDrive[];
  groups: readonly OnboardingProfileGroup[];
  hardware: OnboardingProfileHardware;
  customHardware: readonly OnboardingProfileCustomHardware[];
}

export interface ClientOnboardingConfiguration {
  clientId: string;
  clientName: string;
  clientActive: boolean;
  tokenConfigured: boolean;
  tokenActive: boolean;
  tokenLastRotatedAt: string | null;
  locations: readonly OnboardingLocation[];
  departments: readonly OnboardingNamedItem[];
  profiles: readonly OnboardingProfile[];
}

export interface PublicOnboardingConfiguration {
  clientName: string;
  locations: readonly { id: string; name: string }[];
  departments: readonly { id: string; name: string }[];
  profiles: readonly PublicOnboardingProfile[];
}

/** Public defaults omit internal notes, license names, paths, and group identities. */
export interface PublicOnboardingProfile {
  id: string;
  name: string;
  printers?: readonly { id: string; name: string }[];
  access: Pick<OnboardingProfileAccess, 'microsoft365Required' | 'officeAppsRequired'
    | 'teamsRequired' | 'vpnRequired' | 'phoneRequired' | 'printerAccessRequired'>;
  hardware: Pick<OnboardingProfileHardware, 'desktopRequired' | 'laptopRequired'
    | 'dockingStationRequired' | 'monitorCount' | 'keyboardMouseRequired' | 'headsetRequired'
    | 'deskPhoneRequired' | 'scannerRequired' | 'printerAccessRequired'>;
  hasSoftwareDefaults: boolean;
  hasSharedDriveDefaults: boolean;
  customAccess: readonly { id: string; name: string }[];
  customHardware: readonly { id: string; name: string; quantity: number }[];
}

export interface LocationInput {
  id: string | null;
  name: string;
  address: string | null;
  active: boolean;
}

export interface DepartmentInput {
  id: string | null;
  name: string;
  active: boolean;
}

export interface ProfileInput {
  id: string | null;
  name: string;
  description: string | null;
  active: boolean;
  access: OnboardingProfileAccess;
  /** Omission by an older caller preserves existing printer configuration. */
  printers?: readonly (Omit<OnboardingNamedItem, 'id'> & { id: string | null })[];
  customAccess: readonly (Omit<OnboardingProfileCustomAccess, 'id'> & { id: string | null })[];
  software: readonly (Omit<OnboardingProfileSoftware, 'id'> & { id: string | null })[];
  sharedDrives: readonly (Omit<OnboardingProfileSharedDrive, 'id'> & { id: string | null })[];
  groups: readonly (Omit<OnboardingProfileGroup, 'id'> & { id: string | null })[];
  hardware: OnboardingProfileHardware;
  customHardware: readonly (Omit<OnboardingProfileCustomHardware, 'id'> & { id: string | null })[];
}

export type OnboardingErrorCode =
  | 'INVALID_INPUT' | 'CLIENT_NOT_FOUND' | 'ITEM_NOT_FOUND'
  | 'TOKEN_ALREADY_EXISTS' | 'TOKEN_NOT_FOUND' | 'IDEMPOTENCY_CONFLICT'
  | 'REQUEST_NOT_FOUND' | 'DELIVERY_NOT_RETRYABLE';

export class OnboardingValidationError extends Error {
  constructor(message: string, readonly code: OnboardingErrorCode) {
    super(message);
    this.name = 'OnboardingValidationError';
  }
}

export const PUBLIC_TOKEN_PATTERN = /^[a-f0-9]{64}$/;

export function isValidPublicToken(value: unknown): value is string {
  return typeof value === 'string' && value.length === 64 && PUBLIC_TOKEN_PATTERN.test(value);
}

/** A future submission must validate every selected identifier against this resolved client. */
export function validatePublicSelection(
  configuration: PublicOnboardingConfiguration,
  selection: { locationId: string | null; departmentId: string | null; profileId: string | null },
): void {
  if (selection.locationId !== null
    && !configuration.locations.some((item) => item.id === selection.locationId)) {
    throw new OnboardingValidationError('Location is not available for this client', 'INVALID_INPUT');
  }
  if (selection.departmentId !== null
    && !configuration.departments.some((item) => item.id === selection.departmentId)) {
    throw new OnboardingValidationError('Department is not available for this client', 'INVALID_INPUT');
  }
  if (selection.profileId !== null
    && !configuration.profiles.some((item) => item.id === selection.profileId)) {
    throw new OnboardingValidationError('Profile is not available for this client', 'INVALID_INPUT');
  }
}

export const EMPTY_ACCESS: OnboardingProfileAccess = {
  microsoft365Required: false, microsoft365License: null, officeAppsRequired: false,
  teamsRequired: false, vpnRequired: false, phoneRequired: false,
  printerAccessRequired: false, notes: null,
};

export const DEFAULT_HARDWARE: OnboardingProfileHardware = {
  desktopRequired: false, laptopRequired: false, dockingStationRequired: false,
  monitorCount: 1, keyboardMouseRequired: true, headsetRequired: false,
  deskPhoneRequired: false, scannerRequired: false, printerAccessRequired: false,
  notes: null,
};

export function parseLocationInput(value: unknown): LocationInput {
  const row = record(value, ['id', 'name', 'address', 'active']);
  return {
    id: optionalId(row.id), name: text(row.name, 160),
    address: optionalText(row.address, 300), active: flag(row.active),
  };
}

export function parseDepartmentInput(value: unknown): DepartmentInput {
  const row = record(value, ['id', 'name', 'active']);
  return { id: optionalId(row.id), name: text(row.name, 160), active: flag(row.active) };
}

export function parseProfileInput(value: unknown): ProfileInput {
  const row = record(value, [
    'id', 'name', 'description', 'active', 'access', 'printers', 'customAccess', 'software', 'sharedDrives', 'groups', 'hardware', 'customHardware',
  ]);
  const access = record(row.access, [
    'microsoft365Required', 'microsoft365License', 'officeAppsRequired', 'teamsRequired',
    'vpnRequired', 'phoneRequired', 'printerAccessRequired', 'notes',
  ]);
  const hardware = record(row.hardware, [
    'desktopRequired', 'laptopRequired', 'dockingStationRequired', 'monitorCount',
    'keyboardMouseRequired', 'headsetRequired', 'deskPhoneRequired', 'scannerRequired',
    'printerAccessRequired', 'notes',
  ]);
  const monitorCount = hardware.monitorCount;
  if (typeof monitorCount !== 'number' || !Number.isInteger(monitorCount)
    || monitorCount < 0 || monitorCount > 8) invalid();
  return {
    id: optionalId(row.id), name: text(row.name, 160),
    description: optionalText(row.description, 2_000), active: flag(row.active),
    access: {
      microsoft365Required: flag(access.microsoft365Required),
      microsoft365License: null,
      officeAppsRequired: flag(access.officeAppsRequired), teamsRequired: flag(access.teamsRequired),
      vpnRequired: flag(access.vpnRequired), phoneRequired: flag(access.phoneRequired),
      printerAccessRequired: flag(access.printerAccessRequired), notes: optionalText(access.notes, 2_000),
    },
    ...(row.printers === undefined ? {} : { printers: array(row.printers).map((value) => {
      const item = record(value, ['id', 'name', 'active']);
      return { id: optionalId(item.id), name: text(item.name, 160), active: flag(item.active) };
    }) }),
    customAccess: array(row.customAccess).map((value) => {
      const item = record(value, ['id', 'name', 'notes', 'active']);
      return { id: optionalId(item.id), name: text(item.name, 160),
        notes: optionalText(item.notes, 2_000), active: flag(item.active) };
    }),
    software: array(row.software).map((value) => {
      const item = record(value, ['id', 'softwareName', 'systemDetail', 'installRequired', 'licenseRequired', 'notes']);
      return {
        id: optionalId(item.id), softwareName: text(item.softwareName, 160),
        systemDetail: optionalText(item.systemDetail ?? null, 300),
        installRequired: flag(item.installRequired), licenseRequired: flag(item.licenseRequired),
        notes: optionalText(item.notes, 2_000),
      };
    }),
    sharedDrives: array(row.sharedDrives).map((value) => {
      const item = record(value, ['id', 'driveName', 'drivePath', 'accessLevel', 'notes']);
      return {
        id: optionalId(item.id), driveName: text(item.driveName, 160),
        drivePath: optionalText(item.drivePath, 300),
      };
    }),
    groups: array(row.groups).map((value) => {
      const item = record(value, ['id', 'groupName', 'systemName', 'groupType', 'notes']);
      return {
        id: optionalId(item.id), groupName: text(item.groupName, 160),
        systemName: optionalText(item.systemName, 160), groupType: optionalText(item.groupType, 100),
        notes: optionalText(item.notes, 2_000),
      };
    }),
    hardware: {
      desktopRequired: flag(hardware.desktopRequired), laptopRequired: flag(hardware.laptopRequired),
      dockingStationRequired: flag(hardware.dockingStationRequired), monitorCount,
      keyboardMouseRequired: flag(hardware.keyboardMouseRequired), headsetRequired: flag(hardware.headsetRequired),
      deskPhoneRequired: flag(hardware.deskPhoneRequired), scannerRequired: flag(hardware.scannerRequired),
      printerAccessRequired: flag(hardware.printerAccessRequired), notes: optionalText(hardware.notes, 2_000),
    },
    customHardware: array(row.customHardware).map((value) => {
      const item = record(value, ['id', 'name', 'quantity', 'notes', 'active']);
      const quantity = item.quantity;
      if (typeof quantity !== 'number' || !Number.isInteger(quantity) || quantity < 1 || quantity > 100) invalid();
      return { id: optionalId(item.id), name: text(item.name, 160), quantity,
        notes: optionalText(item.notes, 2_000), active: flag(item.active) };
    }),
  };
}

function invalid(): never {
  throw new OnboardingValidationError('Invalid onboarding configuration', 'INVALID_INPUT');
}

function record(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) invalid();
  const row = value as Record<string, unknown>;
  if (Object.keys(row).some((key) => !keys.includes(key))) invalid();
  return row;
}

function array(value: unknown): readonly unknown[] {
  if (!Array.isArray(value) || value.length > 100) invalid();
  return value as readonly unknown[];
}

function text(value: unknown, max: number): string {
  if (typeof value !== 'string') invalid();
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > max) invalid();
  return trimmed;
}

function optionalText(value: unknown, max: number): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' || value.length > max) invalid();
  return value.trim() || null;
}

function optionalId(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' || !/^[a-zA-Z0-9-]{1,80}$/.test(value)) invalid();
  return value;
}

function flag(value: unknown): boolean {
  if (typeof value !== 'boolean') invalid();
  return value;
}
